// The drawers the connected accounts are filed into (ADR-0029).
//
// This lives on the Connections screen rather than on a settings page, for the
// same reason "who can post to this?" does: it is the question asked while
// looking at the accounts, and an answer a page away is an answer nobody
// changes. The switcher in the top bar reads the same list; this is the one
// place it is edited.
//
// A brand is filing and never authority. Nothing here grants access to anything,
// and the card says so — otherwise "put the client in their own brand" starts to
// read like a security control, which it is not.
//
// ADDING A BRAND ALSO ASKS WHAT IT SAYS. A brand is not only a drawer: it is
// the business whose name, phone number and app store links go into every
// second caption it publishes ({{business}}, {{phone}}, {{play_store}}). The
// day somebody sets one up is the day they know all of it, so the form asks
// then — one call, one transaction — rather than leaving a brand that exists
// and resolves every token to a brace. The fields are optional and the rest of
// them live on the caption library, which is also where they are edited later;
// this form is a good start, not the only door.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "./Spinner";
import { BRAND_NAME_MAX, createBrand, deleteBrand, renameBrand, type Brand } from "../lib/brands";
import { NEW_BRAND_DETAILS } from "../lib/tokens";

export function BrandsCard({
  workspaceId, brands, counts, unfiled, mayOrganise, mayWriteDetails, onChanged,
}: {
  workspaceId: string;
  brands: Brand[];
  /** brand id -> how many accounts are in it. */
  counts: Map<string, number>;
  /** How many accounts are in no brand at all. */
  unfiled: number;
  mayOrganise: boolean;
  /** `post.create` — a brand's details are content, and the same RPC writes
   *  both halves, so without this the form would offer boxes the database
   *  refuses and take the new brand down with them. */
  mayWriteDetails: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [fresh, setFresh] = useState("");
  // What the new brand says, keyed by token name. Starts empty, and stays that
  // way for anyone who just wants a drawer — every field is optional and a
  // blank one is not written at all.
  const [freshDetails, setFreshDetails] = useState<Record<string, string>>({});
  // The detail fields are folded away until asked for. The common act on this
  // card is "add sabu"; the uncommon one is filling in six boxes, and a form
  // that opens with six boxes makes the common act look like work.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const busy = busyKey !== null;

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="card">
      <h2>Brands</h2>
      <p className="muted">
        A brand is a drawer for the accounts that publish as the same thing — one for{" "}
        <em>labtest1</em>, one for <em>sabu</em>. Filing an account here changes what you see
        and nothing else: it grants nobody access, and who may publish to an account is still
        set on that account.
      </p>

      {error && <p className="notice err">{error}</p>}

      {brands.length === 0 && <p className="muted">No brands yet.</p>}

      <ul className="brand-list">
        {brands.map((b) => {
          const count = counts.get(b.id) ?? 0;
          const isEditing = editing === b.id;

          return (
            <li key={b.id} className="brand-row">
              {isEditing ? (
                <form
                  className="row brand-edit"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const name = draft.trim();
                    if (!name || name === b.name) { setEditing(null); return; }
                    void run(`rename:${b.id}`, async () => {
                      await renameBrand(b.id, name);
                      setEditing(null);
                    });
                  }}
                >
                  <input
                    value={draft}
                    maxLength={BRAND_NAME_MAX}
                    autoFocus
                    aria-label={`New name for ${b.name}`}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <button type="submit" className={busyKey === `rename:${b.id}` ? "sm is-busy" : "sm"}
                          disabled={busy || !draft.trim()}>
                    {busyKey === `rename:${b.id}` && <Spinner />}
                    {busyKey === `rename:${b.id}` ? "Saving…" : "Save"}
                  </button>
                  <button type="button" className="sm" disabled={busy}
                          onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  <strong className="brand-name">{b.name}</strong>
                  <span className="muted">
                    {count} account{count === 1 ? "" : "s"}
                  </span>
                  <span className="spacer" />
                  {mayOrganise && (
                    <>
                      <button type="button" className="sm" disabled={busy}
                              onClick={() => { setEditing(b.id); setDraft(b.name); }}>
                        Rename
                      </button>
                      {/* Worth a confirm even though nothing is lost: the
                          accounts come back unfiled, and somebody who expected
                          "delete the brand" to mean "disconnect these" should
                          read that it does not before pressing it. */}
                      <button
                        type="button"
                        className={busyKey === `delete:${b.id}` ? "sm danger is-busy" : "sm danger"}
                        disabled={busy}
                        title={`Remove the brand "${b.name}". Its accounts stay connected and become unfiled.`}
                        onClick={() => {
                          if (!confirm(
                            `Remove the brand "${b.name}"?\n\n`
                            + `Its ${count} account${count === 1 ? "" : "s"} stay connected and `
                            + "keep publishing — they become unfiled.\n\n"
                            + `Anything set for ${b.name} alone — its phone number, its app `
                            + "links — goes with it. Values shared by every brand stay.")) return;
                          void run(`delete:${b.id}`, () => deleteBrand(b.id));
                        }}
                      >
                        {busyKey === `delete:${b.id}` && <Spinner />}
                        {busyKey === `delete:${b.id}` ? "Removing…" : "Remove"}
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
          );
        })}

        {unfiled > 0 && (
          <li className="brand-row muted">
            <strong className="brand-name">Unfiled</strong>
            <span>{unfiled} account{unfiled === 1 ? "" : "s"} in no brand</span>
          </li>
        )}
      </ul>

      {mayOrganise ? (
        <form
          className="brand-new"
          onSubmit={(e) => {
            e.preventDefault();
            const name = fresh.trim();
            if (!name) return;
            void run("create", async () => {
              await createBrand(
                workspaceId,
                name,
                NEW_BRAND_DETAILS.map((d) => ({
                  key: d.key,
                  label: d.label,
                  value: freshDetails[d.key] ?? "",
                  sortOrder: d.sort,
                })),
              );
              // Only on success. A brand that failed to save must leave the six
              // things somebody just typed on the screen, not make them type
              // them again to find out what the error was.
              setFresh("");
              setFreshDetails({});
              setDetailsOpen(false);
            });
          }}
        >
          <div className="row">
            <input
              value={fresh}
              maxLength={BRAND_NAME_MAX}
              placeholder="New brand — e.g. sabu"
              aria-label="Name for a new brand"
              onChange={(e) => setFresh(e.target.value)}
            />
            <button type="submit" className={busyKey === "create" ? "primary is-busy" : "primary"}
                    disabled={busy || !fresh.trim()}>
              {busyKey === "create" && <Spinner />}
              {busyKey === "create" ? "Adding…" : "Add brand"}
            </button>
            {mayWriteDetails && (
              <button type="button" className="sm" disabled={busy}
                      aria-expanded={detailsOpen}
                      onClick={() => setDetailsOpen((open) => !open)}>
                {detailsOpen ? "Hide details" : "Add its details…"}
              </button>
            )}
          </div>

          {detailsOpen && mayWriteDetails && (
            <div className="brand-new-details">
              <p className="muted">
                What this business says in every second post. Write{" "}
                <code>{"{{business}}"}</code> or <code>{"{{play_store}}"}</code> in a caption and
                it becomes the real thing when you pick that caption in Compose — for this brand,
                and not for the others. Every box is optional, and all of them can be changed
                later on the <Link to="/captions">caption library</Link>.
              </p>

              <div className="detail-grid">
                {NEW_BRAND_DETAILS.map((d) => (
                  <label key={d.key}>
                    <span>
                      {d.label} <code className="token">{`{{${d.key}}}`}</code>
                    </span>
                    <input
                      value={freshDetails[d.key] ?? ""}
                      placeholder={d.example}
                      disabled={busy}
                      onChange={(e) =>
                        setFreshDetails((current) => ({ ...current, [d.key]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
        </form>
      ) : (
        <p className="notice">
          Filing accounts into brands is an owner, admin or manager action. You can still
          publish to every account you have been given.
        </p>
      )}
    </div>
  );
}
